import React, { useEffect, useState } from 'react';
import {
  ackNotice, clearError, closeBlocked, done, extend, getState, minutesLeftLabel, mmss, msLeft, noticeAcked,
  overrideAndOpen, refresh, startPolling, subscribe,
} from './hsLock';
import { hsLockApi } from './api';
import { Z_LOCK_STRIP } from './layers';   // 5.49.1 top-bar menus open above this strip

// =============================================================================
// 5.44.0 "HealthSherpa in use" — one strip at the top of every InsureDocs page (Main top bar, the HealthSherpa page,
// the Calendar page). Renders nothing while HealthSherpa is free and there is nothing to tell this user.
//   OTHERS:  "🔒 HealthSherpa in use by <name> — about N min left (until 1:42 PM). Check with <name> before entering."
//            (admins also get "Clear"). It goes away for everyone when the time runs out.
//   HOLDER:  a small dropdown with a live countdown: Done · "How much more time do you need?" [minutes] [Add].
//            When the time runs out the dropdown stays open: "Your time is up — still in HealthSherpa?" Done / Add more
//            time (re-arms the lock unless someone else took it — then it says who has it).
//            Overridden -> "Taken over by <name> at <time>"; cleared by an admin -> "Cleared by <name> at <time>".
//   BLOCKED TRANSFER (hsLock.transfer got a 409): a modal "<name> is in HealthSherpa until <time>. Please reach out to
//            <name> before entering." Cancel / Override (a required tick "I confirmed with <name> …", then it takes the
//            lock and opens HealthSherpa). Closes only by its buttons.
// User NAMES only — never computers or IPs.
// =============================================================================

const AMBER = '#b45309';
const GREEN = '#065f46';
const strip = (bg) => ({ background: bg, color: '#fff', padding: '6px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10,
  flexWrap: 'wrap', width: '100%', boxSizing: 'border-box', flex: 'none', position: 'relative', zIndex: Z_LOCK_STRIP, lineHeight: 1.35 });
const sbtn = (bg = 'rgba(0,0,0,0.25)', disabled) => ({ padding: '4px 10px', border: '1px solid rgba(255,255,255,0.55)', borderRadius: 5, background: bg,
  color: '#fff', fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap' });
const firstName = (n) => (n || '').trim().split(/\s+/)[0] || n || 'them';

function useLock() {
  const [st, setSt] = useState(getState());
  const [, tick] = useState(0);
  useEffect(() => subscribe(setSt), []);
  useEffect(() => startPolling(), []);
  useEffect(() => {                                       // live countdown; at zero ask the server (it settles expiry)
    const id = setInterval(() => {
      tick((n) => n + 1);
      const s = getState().status;
      if (s && s.active && msLeft(s) <= 0) refresh();
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return st;
}

function MinutesAdd({ onAdd, busy, label = 'Add', testid = 'hslock-add' }) {
  const [m, setM] = useState('10');
  const n = Number(m);
  const ok = Number.isInteger(n) && n >= 1 && n <= 240;
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input type="number" min={1} max={240} step={1} value={m} onChange={(e) => setM(e.target.value)} aria-label="Minutes" data-testid="hslock-minutes"
        style={{ width: 64, padding: '4px 6px', borderRadius: 5, border: '1px solid #9ca3af', fontSize: 13 }} />
      <span style={{ fontSize: 12 }}>min</span>
      <button type="button" style={sbtn(undefined, !ok || busy)} disabled={!ok || busy} onClick={() => onAdd(n)} data-testid={testid}>{label}</button>
    </span>
  );
}

function HolderPanel({ st, ms }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const doDone = async () => { setBusy(true); const r = await done(); setBusy(false); if (!r.ok) setMsg(r.detail); else setOpen(false); };
  const add = async (m) => {
    setBusy(true); setMsg('');
    const r = await extend(m);
    setBusy(false);
    setMsg(r.ok ? `Added — you have it until ${getState().status?.ends_label}.` : r.detail);
  };
  return (
    <div style={strip(GREEN)} data-testid="hslock-holder">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="hslock-holder-toggle" aria-expanded={open}
        style={{ ...sbtn(), border: 'none', background: 'transparent', fontSize: 13, padding: 0 }}>
        🔓 You're in HealthSherpa — <span data-testid="hslock-countdown" style={{ fontVariantNumeric: 'tabular-nums' }}>{mmss(ms)}</span> left (until {st.ends_label}) {open ? '▴' : '▾'}
      </button>
      {open && (
        <div data-testid="hslock-holder-menu" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', flexBasis: '100%', paddingTop: 4 }}>
          <button type="button" style={sbtn('#1f2937', busy)} disabled={busy} onClick={doDone} data-testid="hslock-done">Done — I'm out of HealthSherpa</button>
          <span style={{ fontSize: 12 }}>How much more time do you need?</span>
          <MinutesAdd onAdd={add} busy={busy} />
          {msg && <span style={{ fontSize: 12 }} data-testid="hslock-msg">{msg}</span>}
        </div>
      )}
    </div>
  );
}

function TimeUpPanel({ notice, st }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // someone else has it now: "Done" only dismisses this question (it must never clear THEIR lock — even for an admin)
  const doDone = async () => { setBusy(true); if (!(st && st.active && !st.mine)) await done(); ackNotice(notice); setBusy(false); };
  const add = async (m) => {
    setBusy(true); setMsg('');
    const r = await extend(m);
    setBusy(false);
    if (r.ok) ackNotice(notice);
    else if (r.holder) setMsg(`${r.holder.name} has HealthSherpa now (until ${r.ends_label}) — check with ${firstName(r.holder.name)} before you continue.`);
    else setMsg(r.detail);
  };
  return (
    <div style={strip(AMBER)} data-testid="hslock-timeup">
      <strong>⏰ Your HealthSherpa time is up.</strong>
      <span>Are you still in HealthSherpa?</span>
      <button type="button" style={sbtn('#1f2937', busy)} disabled={busy} onClick={doDone} data-testid="hslock-timeup-done">Done — I'm out</button>
      <MinutesAdd onAdd={add} busy={busy} label="Add more time" testid="hslock-timeup-add" />
      {!msg && st && st.active && !st.mine && st.holder && (
        <span style={{ flexBasis: '100%', fontWeight: 600 }} data-testid="hslock-timeup-taken">{st.holder.name} has HealthSherpa now (until {st.ends_label}).</span>
      )}
      {msg && <span style={{ flexBasis: '100%', fontWeight: 600 }} data-testid="hslock-timeup-msg">{msg}</span>}
    </div>
  );
}

function NoticePanel({ notice }) {
  const words = notice.kind === 'overridden'
    ? `Taken over by ${notice.by || 'someone'} at ${notice.at_label}`
    : `Cleared by ${notice.by || 'an admin'} at ${notice.at_label}`;
  return (
    <div style={strip('#374151')} data-testid="hslock-notice" data-kind={notice.kind}>
      <span>🔓 Your HealthSherpa time: <strong>{words}</strong>. If you're still in HealthSherpa, you'll be signed out there.</span>
      <button type="button" style={sbtn()} onClick={() => ackNotice(notice)} data-testid="hslock-notice-ok">OK</button>
    </div>
  );
}

function OthersBanner({ st, ms }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const name = st.holder ? st.holder.name : 'someone';
  const clear = async () => {
    if (!window.confirm(`Clear the HealthSherpa lock held by ${name}?`)) return;
    setBusy(true); setErr('');
    try { await hsLockApi.done(); } catch (e) { setErr(e?.response?.data?.detail || 'Could not clear it'); }
    await refresh();
    setBusy(false);
  };
  return (
    <div style={strip(AMBER)} data-testid="hslock-banner" role="status">
      <span>🔒 HealthSherpa in use by <strong data-testid="hslock-holder-name">{name}</strong> — {minutesLeftLabel(ms)} left (until {st.ends_label}). Check with {firstName(name)} before entering.</span>
      {st.is_admin && <button type="button" style={sbtn(undefined, busy)} disabled={busy} onClick={clear} data-testid="hslock-admin-clear">Clear (admin)</button>}
      {err && <span style={{ fontWeight: 600 }}>{err}</span>}
    </div>
  );
}

function BlockedModal({ b }) {
  const [step, setStep] = useState(1);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = b.holder ? b.holder.name : 'Someone';
  const first = firstName(name);
  const go = () => { setBusy(true); overrideAndOpen(b.url, b.onOpened).finally(() => setBusy(false)); };   // straight from the click
  const btn = (bg, disabled) => ({ padding: '8px 16px', border: 'none', borderRadius: 6, background: bg, color: '#fff', fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      data-testid="hslock-modal-overlay">
      <div role="alertdialog" aria-label="HealthSherpa in use" data-testid="hslock-modal"
        style={{ background: '#fff', color: '#1f2937', borderRadius: 10, width: 470, maxWidth: '100%', padding: 22, boxShadow: '0 12px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>🔒 HealthSherpa is in use</div>
        {step === 1 ? (
          <>
            <div style={{ fontSize: 15, marginBottom: 18 }} data-testid="hslock-modal-text">
              <strong>{name}</strong> is in HealthSherpa until <strong>{b.ends_label}</strong>. Please reach out to {first} before entering.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={btn('#6b7280')} onClick={closeBlocked} data-testid="hslock-modal-cancel">Cancel</button>
              <button type="button" style={btn('#b91c1c')} onClick={() => setStep(2)} data-testid="hslock-modal-override">Override…</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, marginBottom: 10 }}>Taking over signs {first} out of HealthSherpa. Only do this if {first} told you they're done.</div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 18, cursor: 'pointer' }}>
              <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="hslock-override-tick" style={{ marginTop: 3 }} />
              <span>I confirmed with {name} that they're done (they forgot to clear it).</span>
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={btn('#6b7280')} onClick={closeBlocked} data-testid="hslock-modal-cancel2">Cancel</button>
              <button type="button" style={btn('#b91c1c', !ticked || busy)} disabled={!ticked || busy} onClick={go} data-testid="hslock-override-go">
                Override and open HealthSherpa</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// 5.47.0 the HealthSherpa page only: a quiet line after a lock RAN OUT (not Done / cleared / overridden), for 60 minutes
// after the expiry or until someone claims HealthSherpa again (the server stops sending it then).
export function LastUserLine({ last }) {
  if (!last || (last.until && Date.parse(last.until) <= Date.now())) return null;
  return (
    <div style={{ padding: '4px 14px', fontSize: 12, color: '#6b7280', background: 'transparent', flex: 'none' }} data-testid="hslock-last-user">
      Last in HealthSherpa: {last.name} — time ran out at {last.ran_out_label}. They may still be inside.
    </div>
  );
}

export default function HsLockStrip({ showLastUser = false }) {
  const st = useLock();
  const s = st.status;
  const ms = msLeft(s);
  const active = !!(s && s.active && ms > 0);
  // the previous holder's question / notice stays until answered, even when someone else has HealthSherpa now
  const notice = s && s.notice && !noticeAcked(s.notice) && !(active && s.mine)
    ? (s.notice.kind === 'expired' ? <TimeUpPanel notice={s.notice} st={s} /> : <NoticePanel notice={s.notice} />) : null;
  const main = active ? (s.mine ? <HolderPanel st={s} ms={ms} /> : <OthersBanner st={s} ms={ms} />) : null;
  return (
    <>
      {notice}
      {main}
      {showLastUser && !active && s && s.last_user && <LastUserLine last={s.last_user} />}
      {st.error && (
        <div style={strip('#7f1d1d')} data-testid="hslock-error">
          <span>{st.error}</span>
          <button type="button" style={sbtn()} onClick={clearError}>OK</button>
        </div>
      )}
      {st.blocked && <BlockedModal key={`${st.blocked.url}:${st.blocked.ends_at}`} b={st.blocked} />}
    </>
  );
}

// Admin: the lock history on the HealthSherpa Settings tab.
const ACTION_WORDS = { start: 'Started', extend: 'Added time', done: 'Done', auto_expire: 'Ran out', override: 'Override', admin_clear: 'Admin cleared' };
export function HsLockLogCard({ c = {} }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const load = () => hsLockApi.log(300).then((r) => { setRows(r.data.rows); setErr(''); }).catch((e) => setErr(e?.response?.data?.detail || 'Could not load the log'));
  useEffect(() => { load(); }, []);
  const td = { padding: '4px 8px', borderBottom: `1px solid ${c.borderColor || '#ddd'}`, fontSize: 12, verticalAlign: 'top' };
  return (
    <div data-testid="hslock-log">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: c.textSecondary || '#666' }}>Every start, added time, done, run-out, override and admin clear (Central time). Newest first.</span>
        <button type="button" onClick={load} style={{ padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      {rows && rows.length === 0 && <div style={{ fontSize: 12, color: c.textSecondary || '#666' }}>Nothing yet.</div>}
      {rows && rows.length > 0 && (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['When', 'What', 'By', 'Whose lock', 'Minutes', 'Until', 'Note'].map((h) => <th key={h} style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="hslock-log-row" data-action={r.action}>
                  <td style={td}>{r.at_label}</td><td style={td}>{ACTION_WORDS[r.action] || r.action}</td><td style={td}>{r.by || '—'}</td>
                  <td style={td}>{r.holder || '—'}</td><td style={td}>{r.minutes != null ? `+${r.minutes}` : ''}</td><td style={td}>{r.ends_label || ''}</td>
                  <td style={td}>{r.note || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
